import { Fragment, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  businessDocumentsSchema,
  type BusinessDocumentType,
  type BusinessDocumentsFormValues,
} from '~domains/partner-application/entities/application';
import { uploadPartnerApplicationDocument } from '~domains/partner-application/entities/application/api';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';

import { ampli } from '~shared/analytics';
import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputDate } from '@breezeventures/web-ph-components/input-date';
import { zodResolver } from '@hookform/resolvers/zod';

import { businessDocumentSections, FILE_OPTIONS } from './config';

function trackDocumentUploaded(
  documentType: BusinessDocumentType,
  applicationId?: string
) {
  const eventProperties = getApplicationEventProperties(applicationId);

  if (documentType === 'BIR_CERTIFICATE_OR_FORM2303') {
    ampli.mpPaBusinessDocBirUploaded(eventProperties);
  } else if (documentType === 'DTI_CERTIFICATE') {
    ampli.mpPaBusinessDocDtiUploaded(eventProperties);
  } else if (documentType === 'BUSINESS_PERMIT') {
    ampli.mpPaBusinessDocMayorsPermitUploaded(eventProperties);
  } else if (documentType === 'OTHER') {
    ampli.mpPaBusinessDocOtherUploaded(eventProperties);
  }
}

type BusinessDocumentsProps = {
  applicationId?: string;
  initialValues?: BusinessDocumentsFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: BusinessDocumentsFormValues) => Promise<void>;
};

export const BusinessDocuments = ({
  applicationId,
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: BusinessDocumentsProps) => {
  const fileUploadRefs = useRef<Record<string, FileUploadRef | null>>({});

  const methods = useForm<BusinessDocumentsFormValues>({
    resolver: zodResolver(businessDocumentsSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      BUSINESS_PERMIT: [],
      BIR_CERTIFICATE_OR_FORM2303: [],
      DTI_CERTIFICATE: [],
      dtiCertificateExpiryDate: '',
      OTHER: [],
    },
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const handleSubmit = async (values: BusinessDocumentsFormValues) => {
    const isAnyUploading = Object.values(fileUploadRefs.current).some((ref) =>
      ref?.checkIsUploading()
    );
    if (isAnyUploading) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <div className="flex flex-col gap-3 py-6">
          <Typography.Title size="s">
            Upload your business documents
          </Typography.Title>
          <Typography.Text size="m" color="secondary">
            If the document has multiple pages, attach photos of all pages.
          </Typography.Text>
        </div>

        <div className="flex flex-col gap-3">
          {businessDocumentSections.map(
            ({ documentType, title, description, multiple, dateField }) => (
              <Fragment key={documentType}>
                <Controller
                  name={documentType}
                  control={methods.control}
                  render={(props) => (
                    <FileUpload
                      ref={(ref) => {
                        fileUploadRefs.current[documentType] = ref;
                      }}
                      {...props}
                      title={title}
                      description={description}
                      options={{
                        ...FILE_OPTIONS,
                        multiple: multiple,
                      }}
                      onUpload={async (req, opts) => {
                        const result = await uploadPartnerApplicationDocument(
                          req,
                          opts
                        );
                        trackDocumentUploaded(documentType, applicationId);
                        return result;
                      }}
                    />
                  )}
                />
                {dateField && (
                  <Controller
                    name={dateField.name}
                    control={methods.control}
                    render={(props) => (
                      <InputDate {...props} label={dateField.label} />
                    )}
                  />
                )}
              </Fragment>
            )
          )}
        </div>
      </Screen>
    </form>
  );
};
